import { Box } from "@rebass/grid";
import { useDebounce } from "@rentiohq/shared-frontend/dist/hooks/useDebounce";
import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import {
  DEFAULT_STATUSES_REGISTRATION_ACTION_REQUIRED_BRUSSELS,
  DEFAULT_STATUSES_REGISTRATION_ACTION_REQUIRED_CONTRACT,
  DEFAULT_STATUSES_REGISTRATION_ACTION_REQUIRED_LOCATION_DESCRIPTION,
  DEFAULT_STATUSES_REGISTRATION_IN_PROGRESS_BRUSSELS,
  DEFAULT_STATUSES_REGISTRATION_IN_PROGRESS_CONTRACT,
  DEFAULT_STATUSES_REGISTRATION_IN_PROGRESS_LOCATION_DESCRIPTION,
  DEFAULT_STATUSES_REGISTRATION_REGISTERED_BRUSSELS,
  DEFAULT_STATUSES_REGISTRATION_REGISTERED_CONTRACT,
  DEFAULT_STATUSES_REGISTRATION_REGISTERED_LOCATION_DESCRIPTION,
} from "@rentiohq/shared-frontend/dist/redux/count/count.config";
import {
  COUNT_BASE,
  ECountIdentifier,
} from "@rentiohq/shared-frontend/dist/redux/count/count.types";
import * as rentRegistrationActions from "@rentiohq/shared-frontend/dist/reduxV2/rentRegistration/rentRegistration.actions";
import * as rentRegistrationSelectors from "@rentiohq/shared-frontend/dist/reduxV2/rentRegistration/rentRegistration.selectors";
import { EContractMemberTypes } from "@rentiohq/shared-frontend/dist/types/contract.types";
import { IDocument } from "@rentiohq/shared-frontend/dist/types/document.types";
import {
  ERegistrationBrusselsStatus,
  ERegistrationDocumentStatus,
  ERegistrationDocumentType,
  IRegistration,
} from "@rentiohq/shared-frontend/dist/types/registration.types";
import { formatDate } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  CONTRACT_BRUSSELS_LEASE_TYPE_FILTER,
  hasBrusselsRegistration,
  PROPERTY_BRUSSELS_ZIP_FILTER,
} from "@rentiohq/shared-frontend/dist/utils/property.utils";
import { stringToSnakeCase } from "@rentiohq/shared-frontend/dist/utils/string.utils";
import {
  Card,
  EFilterType,
  ESpacings,
  Filters,
  MultipleStagesWrapper,
  Page,
  Registrations as RegistrationsBase,
  Stages,
} from "@rentiohq/web-shared/dist/components";
import { IAction } from "@rentiohq/web-shared/dist/types";
import {
  renderCellContractRoles,
  renderCellProperty,
  renderCellStatus,
} from "@rentiohq/web-shared/dist/utils/registration";
import { getLocationDescriptionActions } from "components/DetailDrawers/Drawers/Registration/Registration.utils";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IRootStore } from "redux/reducers";
import { NumberParam, createEnumParam } from "serialize-query-params";

const CONTRACT = "contract";
const LOC_DESCRIPTION = "location_description";
const BRUSSELS = "brussels";

const ORDER_MAP = [
  "createdAt",

  undefined,
  undefined,

  undefined,
  undefined,

  undefined,
  undefined,
  undefined,

  undefined,
];

const getStatusesFilter = (
  stage: string | undefined,
  type: "contract" | "locationDescription",
) => {
  switch (stage) {
    case "in_progress":
      return type === "contract"
        ? DEFAULT_STATUSES_REGISTRATION_IN_PROGRESS_CONTRACT
        : DEFAULT_STATUSES_REGISTRATION_IN_PROGRESS_LOCATION_DESCRIPTION;

    case "action_required":
      return type === "contract"
        ? DEFAULT_STATUSES_REGISTRATION_ACTION_REQUIRED_CONTRACT
        : DEFAULT_STATUSES_REGISTRATION_ACTION_REQUIRED_LOCATION_DESCRIPTION;

    case "registered":
      return type === "contract"
        ? DEFAULT_STATUSES_REGISTRATION_REGISTERED_CONTRACT
        : DEFAULT_STATUSES_REGISTRATION_REGISTERED_LOCATION_DESCRIPTION;

    default:
      return;
  }
};

const getStatusesFilterBrussels = (stage: string | undefined) => {
  switch (stage) {
    case "in_progress":
      return DEFAULT_STATUSES_REGISTRATION_IN_PROGRESS_BRUSSELS;

    case "action_required":
      return DEFAULT_STATUSES_REGISTRATION_ACTION_REQUIRED_BRUSSELS;

    case "registered":
      return DEFAULT_STATUSES_REGISTRATION_REGISTERED_BRUSSELS;

    default:
      return;
  }
};

export const Registrations = () => {
  const navigate = useNavigate();

  const [queryParams, setQueryParamValue] = useQueryParams({
    stage: createEnumParam(["in_progress", "action_required", "registered"]),
    registrationId: NumberParam,
  });

  // State
  const [query, setQuery] = useState<string | undefined>();
  const [debouncedQuery] = useDebounce(query, 300);

  const [contractStatuses, setContractStatuses] = useState(
    getStatusesFilter(queryParams.stage, "contract"),
  );
  const [locationDescriptionStatuses, setLocationDescriptionStatuses] =
    useState(getStatusesFilter(queryParams.stage, "locationDescription"));
  const [brusselsStatuses, setBrusselsStatuses] = useState(
    getStatusesFilterBrussels(queryParams.stage),
  );

  const HEADINGS = [
    getLocalizedText("system.created"),

    getLocalizedText("system.address"),
    getLocalizedText("system.contract_start_date"),

    getLocalizedText("system.tenants"),
    getLocalizedText("system.owners"),

    getLocalizedText("registration.filter.status", {
      type: getLocalizedText("system.contract").toLowerCase(),
    }),
    getLocalizedText("registration.filter.status", {
      type: getLocalizedText("system.location_description").toLowerCase(),
    }),
    getLocalizedText("registration.filter.status", {
      type: getLocalizedText("system.registration_brussels").toLowerCase(),
    }),

    "",
  ];

  // Redux
  const dispatch = useDispatch();

  const registrationContractsInProgressCount = useSelector(
    (state: IRootStore) =>
      rentRegistrationSelectors.getCount(
        state,
        COUNT_BASE[ECountIdentifier.RegistrationsContractsInProgress],
      ),
  );

  const registrationContractsActionRequiredCount = useSelector(
    (state: IRootStore) =>
      rentRegistrationSelectors.getCount(
        state,
        COUNT_BASE[ECountIdentifier.RegistrationsContractsActionRequired],
      ),
  );

  const registrationContractsRegisteredCount = useSelector(
    (state: IRootStore) =>
      rentRegistrationSelectors.getCount(
        state,
        COUNT_BASE[ECountIdentifier.RegistrationsContractsRegistered],
      ),
  );

  const registrationLocationDescriptionInProgressCount = useSelector(
    (state: IRootStore) =>
      rentRegistrationSelectors.getCount(
        state,
        COUNT_BASE[ECountIdentifier.RegistrationLocationDescriptionInProgress],
      ),
  );
  const registrationLocationDescriptionActionRequiredCount = useSelector(
    (state: IRootStore) =>
      rentRegistrationSelectors.getCount(
        state,
        COUNT_BASE[
          ECountIdentifier.RegistrationLocationDescriptionActionRequired
        ],
      ),
  );
  const registrationLocationDescriptionRegisteredCount = useSelector(
    (state: IRootStore) =>
      rentRegistrationSelectors.getCount(
        state,
        COUNT_BASE[ECountIdentifier.RegistrationLocationDescriptionRegistered],
      ),
  );

  const registrationBrusselsInProgressCount = useSelector((state: IRootStore) =>
    rentRegistrationSelectors.getCount(
      state,
      COUNT_BASE[ECountIdentifier.RegistrationBrusselsInProgress],
    ),
  );
  const registrationBrusselsActionRequiredCount = useSelector(
    (state: IRootStore) =>
      rentRegistrationSelectors.getCount(
        state,
        COUNT_BASE[ECountIdentifier.RegistrationBrusselsActionRequired],
      ),
  );
  const registrationBrusselsRegisteredCount = useSelector((state: IRootStore) =>
    rentRegistrationSelectors.getCount(
      state,
      COUNT_BASE[ECountIdentifier.RegistrationBrusselsRegistered],
    ),
  );

  // Data
  const fetchCounts = () => {
    dispatch(
      rentRegistrationActions.getCountStart.getAction({
        query: COUNT_BASE[ECountIdentifier.RegistrationsContractsInProgress],
      }),
    );
    dispatch(
      rentRegistrationActions.getCountStart.getAction({
        query:
          COUNT_BASE[ECountIdentifier.RegistrationsContractsActionRequired],
      }),
    );
    dispatch(
      rentRegistrationActions.getCountStart.getAction({
        query: COUNT_BASE[ECountIdentifier.RegistrationsContractsRegistered],
      }),
    );

    dispatch(
      rentRegistrationActions.getCountStart.getAction({
        query:
          COUNT_BASE[
            ECountIdentifier.RegistrationLocationDescriptionInProgress
          ],
      }),
    );
    dispatch(
      rentRegistrationActions.getCountStart.getAction({
        query:
          COUNT_BASE[
            ECountIdentifier.RegistrationLocationDescriptionActionRequired
          ],
      }),
    );
    dispatch(
      rentRegistrationActions.getCountStart.getAction({
        query:
          COUNT_BASE[
            ECountIdentifier.RegistrationLocationDescriptionRegistered
          ],
      }),
    );

    dispatch(
      rentRegistrationActions.getCountStart.getAction({
        query: COUNT_BASE[ECountIdentifier.RegistrationBrusselsInProgress],
      }),
    );
    dispatch(
      rentRegistrationActions.getCountStart.getAction({
        query: COUNT_BASE[ECountIdentifier.RegistrationBrusselsActionRequired],
      }),
    );
    dispatch(
      rentRegistrationActions.getCountStart.getAction({
        query: COUNT_BASE[ECountIdentifier.RegistrationBrusselsRegistered],
      }),
    );
  };

  useEffect(() => {
    fetchCounts();
  }, []);

  // Set default filters
  const handleApplyContractsInProgressFilter = () => {
    setLocationDescriptionStatuses(undefined);
    setBrusselsStatuses(undefined);
    setContractStatuses(DEFAULT_STATUSES_REGISTRATION_IN_PROGRESS_CONTRACT);
  };

  const handleApplyContractsRegisteredFilter = () => {
    setLocationDescriptionStatuses(undefined);
    setBrusselsStatuses(undefined);
    setContractStatuses(DEFAULT_STATUSES_REGISTRATION_REGISTERED_CONTRACT);
  };

  const handleApplyContractsActionRequiredFilter = () => {
    setLocationDescriptionStatuses(undefined);
    setBrusselsStatuses(undefined);
    setContractStatuses(DEFAULT_STATUSES_REGISTRATION_ACTION_REQUIRED_CONTRACT);
  };

  const handleApplyLocationDescriptionInProgressFilter = () => {
    setContractStatuses(undefined);
    setBrusselsStatuses(undefined);
    setLocationDescriptionStatuses(
      DEFAULT_STATUSES_REGISTRATION_IN_PROGRESS_LOCATION_DESCRIPTION,
    );
  };
  const handleApplyLocationDescriptionRegisteredFilter = () => {
    setContractStatuses(undefined);
    setBrusselsStatuses(undefined);
    setLocationDescriptionStatuses(
      DEFAULT_STATUSES_REGISTRATION_REGISTERED_LOCATION_DESCRIPTION,
    );
  };
  const handleApplyLocationDescriptionActionRequiredFilter = () => {
    setContractStatuses(undefined);
    setBrusselsStatuses(undefined);
    setLocationDescriptionStatuses([
      ERegistrationDocumentStatus.None,
      ...DEFAULT_STATUSES_REGISTRATION_ACTION_REQUIRED_LOCATION_DESCRIPTION,
    ]);
  };

  const handleApplyBrusselsInProgressFilter = () => {
    setContractStatuses(undefined);
    setLocationDescriptionStatuses(undefined);
    setBrusselsStatuses(DEFAULT_STATUSES_REGISTRATION_IN_PROGRESS_BRUSSELS);
  };
  const handleApplyBrusselsRegisteredFilter = () => {
    setContractStatuses(undefined);
    setLocationDescriptionStatuses(undefined);
    setBrusselsStatuses(DEFAULT_STATUSES_REGISTRATION_REGISTERED_BRUSSELS);
  };
  const handleApplyBrusselsActionRequiredFilter = () => {
    setContractStatuses(undefined);
    setLocationDescriptionStatuses(undefined);
    setBrusselsStatuses(DEFAULT_STATUSES_REGISTRATION_ACTION_REQUIRED_BRUSSELS);
  };

  const filter = (() => {
    const statuses: { [key: string]: any }[] = [];

    if (contractStatuses) {
      statuses.push({
        rentContractStatus: {
          in: contractStatuses,
        },
      });
    }

    if (locationDescriptionStatuses) {
      statuses.push({
        locationDescriptionStatus: {
          in: locationDescriptionStatuses,
        },
      });
    }

    if (brusselsStatuses) {
      statuses.push({
        brusselsStatus: {
          in: brusselsStatuses,
        },
        property: PROPERTY_BRUSSELS_ZIP_FILTER,
        contract: CONTRACT_BRUSSELS_LEASE_TYPE_FILTER,
      });
    }

    const whereStatuses: Record<
      number,
      | undefined
      | { [key: string]: any }[]
      | { [key: string]: { [key: string]: any }[] }
    > = {
      [statuses.length]: { or: statuses },
      1: statuses[0],
      0: undefined,
    };

    return {
      search: debouncedQuery,
      where: whereStatuses[statuses.length],
    };
  })();

  // Event handlers
  const handleQueryChange = (value: string) => {
    if (value.length === 0) {
      setQuery(undefined);
      return;
    }

    setQuery(value);
  };

  const handleQueryClear = () => {
    setQuery(undefined);
  };

  const handleClearFilter = () => {
    setQuery(undefined);
    setContractStatuses(undefined);
    setLocationDescriptionStatuses(undefined);
    setBrusselsStatuses(undefined);
  };

  const handleStatusChange =
    (key: "contract" | "location_description") =>
    (value: ERegistrationDocumentStatus[] | ERegistrationDocumentStatus) => {
      if (!Array.isArray(value)) {
        return;
      }

      if (key === CONTRACT) {
        setContractStatuses(value.length > 0 ? value : undefined);
        return;
      }

      if (key === LOC_DESCRIPTION) {
        setLocationDescriptionStatuses(value.length > 0 ? value : undefined);
        return;
      }
    };

  const handleStatusChangeBrussels = (
    value: ERegistrationBrusselsStatus[] | ERegistrationBrusselsStatus,
  ) => {
    if (!Array.isArray(value)) {
      return;
    }

    setBrusselsStatuses(value.length > 0 ? value : undefined);
  };

  // Render
  const renderFilters = () => {
    const translateStatus = (text: any) =>
      getLocalizedText(
        `registration.status.${stringToSnakeCase(text)}`.toLowerCase(),
      );

    return (
      <Box mb={ESpacings.loose}>
        <Filters
          queryValue={query || ""}
          queryPlaceholder={getLocalizedText(
            "follow_up.registrations.filter.query.placeholder",
          )}
          onQueryChange={handleQueryChange}
          onQueryClear={handleQueryClear}
          onClearAll={handleClearFilter}
          filterConfigs={[
            {
              label: getLocalizedText("registration.filter.status", {
                type: getLocalizedText(
                  `system.${CONTRACT}`.toLowerCase(),
                ).toLowerCase(),
              }),
              groupKey: CONTRACT,
              filters: [
                {
                  type: EFilterType.MultiSelect,
                  values: contractStatuses,
                  options: Object.values(ERegistrationDocumentStatus).filter(
                    x => x !== ERegistrationDocumentStatus.None,
                  ),
                  onChange: handleStatusChange(CONTRACT),
                  translate: translateStatus,
                  filterKey: CONTRACT,
                  appliedFiltersPrefix: `${getLocalizedText(
                    "follow_up.registrations.contracts",
                  )}: `,
                  onRemove: key => {
                    if (contractStatuses) {
                      handleStatusChange(CONTRACT)(
                        contractStatuses.filter(status => status !== key),
                      );
                    }
                  },
                },
              ],
            },
            {
              label: getLocalizedText("registration.filter.status", {
                type: getLocalizedText(
                  `system.${LOC_DESCRIPTION}`,
                ).toLowerCase(),
              }),
              groupKey: LOC_DESCRIPTION,
              filters: [
                {
                  type: EFilterType.MultiSelect,
                  values: locationDescriptionStatuses,
                  options: Object.values(ERegistrationDocumentStatus),
                  onChange: handleStatusChange(LOC_DESCRIPTION),
                  translate: translateStatus,
                  filterKey: LOC_DESCRIPTION,
                  appliedFiltersPrefix: `${getLocalizedText(
                    "follow_up.registrations.location_description",
                  )}: `,
                  onRemove: key => {
                    if (locationDescriptionStatuses) {
                      handleStatusChange(LOC_DESCRIPTION)(
                        locationDescriptionStatuses.filter(
                          status => status !== key,
                        ),
                      );
                    }
                  },
                },
              ],
            },
            {
              label: getLocalizedText("registration.filter.status", {
                type: getLocalizedText(`system.${BRUSSELS}`).toLowerCase(),
              }),
              groupKey: BRUSSELS,
              filters: [
                {
                  type: EFilterType.MultiSelect,
                  values: brusselsStatuses,
                  options: Object.values(ERegistrationDocumentStatus),
                  onChange: handleStatusChangeBrussels,
                  translate: translateStatus,
                  filterKey: LOC_DESCRIPTION,
                  appliedFiltersPrefix: `${getLocalizedText(
                    "follow_up.registrations.brussels",
                  )}: `,
                  onRemove: key => {
                    if (brusselsStatuses) {
                      handleStatusChangeBrussels(
                        brusselsStatuses.filter(status => status !== key),
                      );
                    }
                  },
                },
              ],
            },
          ]}
        />
      </Box>
    );
  };

  const renderRowContent = (registration: IRegistration) => [
    formatDate(registration.createdAt),

    renderCellProperty(registration.propertyId, registration.property),
    registration.contract?.startDate
      ? formatDate(registration.contract?.startDate)
      : "-",

    renderCellContractRoles(registration, [
      EContractMemberTypes.Tenant,
      EContractMemberTypes.Parent,
    ]),
    renderCellContractRoles(registration, [EContractMemberTypes.Owner]),

    renderCellStatus(
      registration.rentContractStatus || ERegistrationDocumentStatus.None,
      // TODO: @Arno type of IRegistrationDocument is the same as IDocument?
      registration.documents[0] as unknown as IDocument,
    ),
    renderCellStatus(
      registration.locationDescriptionStatus ||
        ERegistrationDocumentStatus.None,
    ),
    renderCellStatus(
      registration.property &&
        registration.contract &&
        hasBrusselsRegistration(
          registration.property,
          registration.contract.leaseType,
        )
        ? registration.brusselsStatus || ERegistrationBrusselsStatus.None
        : ERegistrationDocumentStatus.NotApplicable,
    ),
  ];

  const getRowActions = (registration: IRegistration) => {
    let result: IAction[][] = [];

    result.push([
      {
        id: "view",
        content: getLocalizedText("system.view_detail"),
        onClick: () => {
          setQueryParamValue({ registrationId: registration.id });
        },
      },
    ]);

    result.push(
      getLocationDescriptionActions({
        registration,
        property: registration.property,
        navigate,
        dispatch,
      }),
    );

    if (
      registration.rentContractStatus === ERegistrationDocumentStatus.Registered
    ) {
      result.push([
        {
          id: "view",
          content: getLocalizedText("registration.addendum.title"),
          onClick: () => {
            navigate(
              `/properties/${registration.propertyId}/contracts/${registration?.contractId}/registration?id=${registration.id}&documentType=${ERegistrationDocumentType.Addendum}`,
            );
          },
        },
      ]);
    }

    return result;
  };

  return (
    <Page
      fullWidth
      title={getLocalizedText("follow_up.registrations.page.title")}
      metadata={getLocalizedText("follow_up.registrations.page.description")}
      breadcrumbs={{
        to: "/",
        content: getLocalizedText("system.back"),
      }}
    >
      <Card>
        <MultipleStagesWrapper>
          <Stages
            stages={[
              {
                heading: getLocalizedText(
                  "follow_up.registration_documents.stage.in_progress",
                ),
                countDescription: getLocalizedText("system.entity.contracts"),
                icon: "contentPenWrite",
                count: registrationContractsInProgressCount || 0,
                onClick: handleApplyContractsInProgressFilter,
              },

              {
                heading: getLocalizedText(
                  "follow_up.registration_documents.stage.action_required",
                ),
                countDescription: getLocalizedText("system.entity.contracts"),
                appearance: "warning",
                icon: "contentPenWrite",
                count: registrationContractsActionRequiredCount || 0,
                onClick: handleApplyContractsActionRequiredFilter,
              },

              {
                heading: getLocalizedText(
                  "follow_up.registration_documents.stage.registered",
                ),
                countDescription: getLocalizedText("system.entity.contracts"),
                icon: "billStack",
                count: registrationContractsRegisteredCount || 0,

                onClick: handleApplyContractsRegisteredFilter,
              },
            ]}
          />

          <Stages
            stages={[
              {
                heading: getLocalizedText(
                  "follow_up.registration_documents.stage.in_progress",
                ),
                countDescription: getLocalizedText(
                  "system.location_description",
                ),
                icon: "contentPenWrite",
                count: registrationLocationDescriptionInProgressCount || 0,
                onClick: handleApplyLocationDescriptionInProgressFilter,
              },

              {
                heading: getLocalizedText(
                  "follow_up.registration_documents.stage.action_required",
                ),
                countDescription: getLocalizedText(
                  "system.location_description",
                ),
                appearance: "warning",
                icon: "contentPenWrite",
                count: registrationLocationDescriptionActionRequiredCount || 0,
                onClick: handleApplyLocationDescriptionActionRequiredFilter,
              },

              {
                heading: getLocalizedText(
                  "follow_up.registration_documents.stage.registered",
                ),
                countDescription: getLocalizedText(
                  "system.location_description",
                ),
                icon: "billStack",
                count: registrationLocationDescriptionRegisteredCount || 0,
                onClick: handleApplyLocationDescriptionRegisteredFilter,
              },
            ]}
          />

          <Stages
            stages={[
              {
                heading: getLocalizedText(
                  "follow_up.registration_documents.stage.in_progress",
                ),
                countDescription: getLocalizedText(
                  "system.registration_brussels",
                ),
                icon: "contentPenWrite",
                count: registrationBrusselsInProgressCount || 0,
                onClick: handleApplyBrusselsInProgressFilter,
              },

              {
                heading: getLocalizedText(
                  "follow_up.registration_documents.stage.action_required",
                ),
                countDescription: getLocalizedText(
                  "system.registration_brussels",
                ),
                appearance: "warning",
                icon: "contentPenWrite",
                count: registrationBrusselsActionRequiredCount || 0,
                onClick: handleApplyBrusselsActionRequiredFilter,
              },

              {
                heading: getLocalizedText(
                  "follow_up.registration_documents.stage.registered",
                ),
                countDescription: getLocalizedText(
                  "system.registration_brussels",
                ),
                icon: "billStack",
                count: registrationBrusselsRegisteredCount || 0,
                onClick: handleApplyBrusselsRegisteredFilter,
              },
            ]}
          />
        </MultipleStagesWrapper>
      </Card>

      <RegistrationsBase
        filter={filter}
        headings={HEADINGS}
        orderMap={ORDER_MAP}
        columnContentTypes={Array(ORDER_MAP.length).fill("text")}
        renderFilters={renderFilters}
        renderRowContent={renderRowContent}
        getRowActions={getRowActions}
      />
    </Page>
  );
};
